import type { Apariencia, PrimerosPasosEstado, ResumenPrueba, TareaPequenaId, TaxProfile } from '@alexiarest/shared';
import { useEffect } from 'react';
import {
  type PurchaseDocumentType, normalizeText, addMonths, type ExpenseImportItem, type KitchenLink, type KitchenShareView, type ContextoAlta, SHARED_LOCATION_ID, effectiveLocationId } from '@alexiarest/shared';
import type {
  CuadreFuente,
  RevenueFill,
  CuadreFuenteInput,
  CuadreKind,
  ImportMappingInput,
  ImportMappingKind,
  SavedImportMapping,
} from '@alexiarest/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AdminOverview,
  PlatformExpense,
  PlatformExpenseInput,
  PlatformPnlResponse,
  PlatformRecurringExpense,
  PlatformRecurringExpenseInput,
  PlatformSettings,
  CashMovementInput,
  DailyRevenueInput,
  ExpenseInput,
  IngredientInput,
  InvestmentInput,
  InventoryCount,
  InventoryCountInput,
  InventoryCountKind,
  InventoryResult,
  DishSalesInput,
  DishSalesPeriod,
  DishSalesSource,
  MenuItemInput,
  Period,
  PosIntegrationPatch,
  PosIntegrationsResponse,
  PosIntegrationView,
  PosProvider,
  PosSyncResult,
  PosUnitsResult,
  RecipeInput,
  TargetInput,
  TenantSettings,
  BillingInterval,
} from '@alexiarest/shared';
import { api, isDemoMode } from './client';
import { ApiError } from './error';
import { useLocationScope } from '../context/LocationContext';
import type {
  BatchResult,
  ConfirmRecipeProposalInput,
  ConfirmRecipeProposalResponse,
  IngredientBatch,
  RecipeBatch,
  RecipeProposalResponse,
  SupplierInput,
  MergeSuppliersResult,
  ScanQuotaResponse,
  InventoryScanResult,
  InventoryIngredientsByLocation,
  ScanResult,
  HoldedReintento,
  LocationConfigPatch,
  StandingTargetResponse,
} from './contract';
import type {
  AmortizationRow,
  AssignableRole,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantSuggestion,
  CashFlowResponse,
  CashMovement,
  CostDeviation,
  DailyRevenue,
  DashboardResponse,
  ExpenseEntry,
  Ingredient,
  Investment,
  CustomRole,
  CustomRoleInput,
  InviteInput,
  KpisResponse,
  Location,
  MapeoVentasPropuesta,
  RejillaArchivo,
  MeResponse,
  CampanaNovedadesEstado,
  Membership,
  MenuItem,
  PayablesResponse,
  PricingAnalysis,
  PricingResponse,
  ProfitAndLoss,
  Recipe,
  RecipeCost,
  RecipeCostsResponse,
  ReconciliationResult,
  Supplier,
  Target,
  TargetComparison,
  TaxAllocation,
  TaxAllocationRequest,
  TeamResponse,
  Tenant,
  TenantUpdate,
  WeekdayPerformance,
} from './types';

/* ------------------------------------------------------------------ */
/* Claves de cache                                                     */
/* ------------------------------------------------------------------ */

export const keys = {
  me: ['me'] as const,
  settings: ['settings'] as const,
  ingredients: ['ingredients'] as const,
  suppliers: ['suppliers'] as const,
  recipes: ['recipes'] as const,
  recipe: (id: string) => ['recipe', id] as const,
  recipeCost: (id: string) => ['recipe-cost', id] as const,
  recipeCosts: ['recipe-costs'] as const,
  menuItems: ['menu-items'] as const,
  pricing: (locationId?: string, salesRange?: Period) =>
    ['pricing', locationId ?? 'all', salesRange?.start ?? 'ultima', salesRange?.end ?? 'carga'] as const,
  dishSales: ['dish-sales'] as const,
  inventoryResult: (month: string, locationId: string | null, kind?: string, date?: string) =>
    ['inventory-result', locationId ?? 'todos', month, kind ?? 'cierre', date ?? ''] as const,
  inventoryIngredients: ['inventory-ingredients'] as const,
  platforms: (id: string) => ['platform-comparison', id] as const,
  deviations: ['cost-deviations'] as const,
  revenues: (period: Period, locationId?: string) => ['revenues', locationId ?? 'all', period.start, period.end] as const,
  expenses: (period: Period, locationId?: string) => ['expenses', locationId ?? 'all', period.start, period.end] as const,
  reconciliationCandidates: (invoiceId: string, locationId?: string) =>
    ['reconciliation-candidates', locationId ?? 'all', invoiceId] as const,
  payables: (today: string, locationId?: string) => ['payables', locationId ?? 'all', today] as const,
  investments: (locationId?: string) => ['investments', locationId ?? 'all'] as const,
  amortization: (month: string, locationId?: string) => ['amortization', locationId ?? 'all', month] as const,
  cashMovements: (period: Period, locationId?: string) => ['cash-movements', locationId ?? 'all', period.start, period.end] as const,
  target: (month: string, locationId?: string) => ['target', locationId ?? 'principal', month] as const,
  inventory: ['inventory'] as const,
  inventoryMonth: (month: string, locationId?: string) => ['inventory-month', locationId ?? 'all', month] as const,
  targetComparison: (month: string, locationId?: string) => ['target-comparison', locationId ?? 'all', month] as const,
  pnl: (period: Period, locationId?: string) => ['pnl', locationId ?? 'all', period.start, period.end] as const,
  pnlMonthly: (period: Period, locationId?: string) => ['pnl-monthly', locationId ?? 'all', period.start, period.end] as const,
  cashflow: (period: Period, locationId?: string) => ['cashflow', locationId ?? 'all', period.start, period.end] as const,
  kpis: (period: Period, locationId?: string) => ['kpis', locationId ?? 'all', period.start, period.end] as const,
  weekday: (period: Period, locationId?: string) => ['weekday', locationId ?? 'all', period.start, period.end] as const,
  dashboard: (period: Period, locationId?: string) => ['dashboard', locationId ?? 'all', period.start, period.end] as const,
  assistantSuggestions: (period: Period, locationId?: string) =>
    ['assistant-suggestions', locationId ?? 'all', period.start, period.end] as const,
  adminOverview: ['admin-overview'] as const,
  adminPnl: (year: number) => ['admin-pnl', year] as const,
  team: ['team'] as const,
  customRoles: ['team', 'roles'] as const,
  locations: ['locations'] as const,
  posIntegrations: ['pos-integrations'] as const,
};

function invalidateAll(qc: QueryClient, roots: string[][]): void {
  for (const key of roots) void qc.invalidateQueries({ queryKey: key });
}

/** Todo lo que depende del escandallo: recetas, ingredientes y precios. */
export function invalidateCosting(qc: QueryClient): void {
  invalidateAll(qc, [
    ['ingredients'],
    ['recipes'],
    ['recipe'],
    ['recipe-cost'],
    // La lista de Recetas usa una clave distinta (`keys.recipeCosts`): sin
    // esto seguia ordenando por costes viejos tras cambiar un precio.
    ['recipe-costs'],
    ['menu-items'],
    ['pricing'],
    ['platform-comparison'],
    ['cost-deviations'],
    ['dashboard'],
    // El recuento usa el costo de las recetas (teórico) y el catálogo (qué se cuenta).
    ['inventory-result'],
    ['inventory-ingredients'],
  ]);
}

/** Todo lo que depende de los movimientos: P&L, tesoreria y KPIs. */
export function invalidateFinance(qc: QueryClient): void {
  invalidateAll(qc, [
    ['revenues'],
    ['expenses'],
    ['reconciliation-candidates'],
    ['payables'],
    ['investments'],
    ['amortization'],
    // Bloque I: préstamos, gastos que parecen inversiones y objetivos para siempre.
    ['loans'],
    ['looks-like-investments'],
    ['standing-target'],
    ['cash-movements'],
    ['target'],
    ['target-comparison'],
    ['inventory'],
    ['inventory-month'],
    ['inventory-result'],
    ['inventory-ingredients'],
    ['pnl'],
    ['pnl-monthly'],
    ['cashflow'],
    ['kpis'],
    ['weekday'],
    ['dashboard'],
  ]);
}

/* ------------------------------------------------------------------ */
/* Cuenta y ajustes                                                    */
/* ------------------------------------------------------------------ */

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({ queryKey: keys.me, queryFn: () => api.getMe() });
}

/**
 * Altas sin zona horaria (13 oct 2026): se manda una vez la del navegador y
 * la API la guarda solo si falta. Sin aviso: el usuario no tiene que hacer nada.
 */
const zonaEnviada = new Set<string>();
export function useZonaHorariaAutomatica(me: MeResponse | undefined): void {
  const qc = useQueryClient();
  const tenantId = me?.tenant?.tenantId;
  const falta = Boolean(me?.tenant && !me.tenant.timezone);
  const puede = !me?.user?.customRoleId && (me?.user?.role === 'owner' || me?.user?.role === 'admin');
  useEffect(() => {
    if (!tenantId || !falta || !puede || zonaEnviada.has(tenantId)) return;
    let zona: string | undefined;
    try { zona = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { zona = undefined; }
    if (!zona) return;
    zonaEnviada.add(tenantId);
    api.fillMissingTimezone(zona).then(({ timezone }) => {
      if (!timezone) return;
      qc.setQueryData<MeResponse>(keys.me, (prev) => (prev?.tenant ? { ...prev, tenant: { ...prev.tenant, timezone } } : prev));
    }).catch(() => undefined);
  }, [tenantId, falta, puede, qc]);
}

export function useLocations(): UseQueryResult<Location[]> {
  return useQuery({ queryKey: keys.locations, queryFn: () => api.listLocations() });
}

export function useCreateLocation(): UseMutationResult<Location, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createLocation(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.locations });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useUpdateLocation(): UseMutationResult<
  Location,
  Error,
  { locationId: string; name: string; active: boolean; operatingWeekdays?: number[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, ...input }) => api.updateLocation(locationId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.locations });
      void qc.invalidateQueries({ queryKey: keys.me });
      invalidateFinance(qc);
    },
  });
}

/**
 * Convierte el campo `photo` de una receta en un src pintable.
 * - dataURL (demo y fotos antiguas): se usa tal cual.
 * - clave de S3 ("photos/…"): se pide la URL firmada a la API y se cachea
 *   45 min (la firma dura 60: margen para no pintar firmas caducadas).
 */
export function usePhotoSrc(photo: string | undefined): string | undefined {
  const isKey = Boolean(photo?.startsWith('photos/'));
  const query = useQuery({
    queryKey: ['photo-url', photo],
    queryFn: () => api.getPhotoUrl(photo as string),
    enabled: isKey,
    staleTime: 45 * 60_000,
    gcTime: 60 * 60_000,
  });
  return isKey ? query.data : photo;
}

export function useUpdateTenant(): UseMutationResult<Tenant, Error, TenantUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TenantUpdate) => api.updateTenant(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Tour guiado visto o saltado por este usuario: se refresca /me para no repetirlo. */
export function useMarkTourSeen(): UseMutationResult<{ tourSeenAt: string }, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markTourSeen(),
    // Se refresca /me pase lo que pase: si el servidor no lo ha guardado, el
    // tour volvera a salir y es mejor saberlo que creer que esta hecho.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/**
 * «Entendido» en un aviso de la primera vez (15 oct 2026). Se pinta como
 * visto al momento (optimista) y se guarda por persona en la API.
 */
export function useMarkAvisoVisto(): UseMutationResult<{ avisosVistos: string[] }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.markAvisoVisto(id),
    onMutate: (id: string) => {
      qc.setQueryData<MeResponse>(keys.me, (me) => (me ? { ...me, user: { ...me.user, avisosVistos: [...new Set([...(me.user.avisosVistos ?? []), id])] } } : me));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** «Para empezar»: marca una tarea pequeña o cierra los primeros pasos. */
export function useMarcarPrimerosPasos(): UseMutationResult<PrimerosPasosEstado, Error, { hecho?: TareaPequenaId; terminado?: true }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { hecho?: TareaPequenaId; terminado?: true }) => api.marcarPrimerosPasos(input),
    onSuccess: (primerosPasos) => {
      qc.setQueryData<MeResponse>(keys.me, (me) => (me ? { ...me, tenant: { ...me.tenant, primerosPasos } } : me));
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useSettings(enabled = true): UseQueryResult<TenantSettings> {
  return useQuery({ queryKey: keys.settings, queryFn: () => api.getSettings(), enabled });
}

export function useSaveSettings(): UseMutationResult<TenantSettings, Error, TenantSettings> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSettings) => api.updateSettings(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings });
      invalidateCosting(qc);
      invalidateFinance(qc);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Ingredientes y proveedores                                          */
/* ------------------------------------------------------------------ */

export function useIngredients(enabled = true): UseQueryResult<Ingredient[]> {
  return useQuery({ queryKey: keys.ingredients, queryFn: () => api.listIngredients(), enabled });
}

export function useSuppliers(enabled = true): UseQueryResult<Supplier[]> {
  return useQuery({ queryKey: keys.suppliers, queryFn: () => api.listSuppliers(), enabled });
}

export interface SaveIngredientVars {
  id?: string;
  input: IngredientInput;
}

export function useSaveIngredient(): UseMutationResult<Ingredient, Error, SaveIngredientVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveIngredientVars) =>
      id ? api.updateIngredient(id, input) : api.createIngredient(input),
    onSuccess: (saved) => {
      // Al momento en la lista: la ficha de una receta costea con ella en el
      // cliente (revisión del 12 oct 2026) y no espera a que vuelva la consulta.
      qc.setQueryData<Ingredient[]>(keys.ingredients, (lista) =>
        lista ? [...lista.filter((i) => i.ingredientId !== saved.ingredientId), saved] : lista,
      );
      invalidateCosting(qc);
    },
  });
}

export function useDeleteIngredient(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteIngredient(id),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useRenameIngredient() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ id, name }: { id: string; name: string }) => api.renameIngredient(id, name), onSuccess: () => invalidateCosting(qc) });
}

export interface SaveSupplierVars {
  id?: string;
  input: SupplierInput;
}

export function useSaveSupplier(): UseMutationResult<Supplier, Error, SaveSupplierVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveSupplierVars) =>
      id ? api.updateSupplier(id, input) : api.createSupplier(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.suppliers });
    },
  });
}

/* Fase 7 · proveedores de verdad: archivar, «son distintos» y unir. */
export function useArchiveSupplier(): UseMutationResult<Supplier, Error, { id: string; archived: boolean }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => api.archiveSupplier(id, archived),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.suppliers }),
  });
}

export function useMarkSuppliersDistinct(): UseMutationResult<void, Error, { id: string; otherId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, otherId }: { id: string; otherId: string }) => api.markSuppliersDistinct(id, otherId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.suppliers }),
  });
}

export function useMergeSuppliers(): UseMutationResult<MergeSuppliersResult, Error, { intoId: string; fromId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ intoId, fromId }: { intoId: string; fromId: string }) => api.mergeSuppliers(intoId, fromId),
    // Mueve ingredientes y gastos: todo lo que enseña el proveedor de algo.
    onSuccess: () => {
      invalidateCosting(qc);
      invalidateAll(qc, [['suppliers'], ['expenses'], ['payables'], ['dashboard']]);
    },
  });
}

export function useDeleteSupplier(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSupplier(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.suppliers });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Recetas                                                             */
/* ------------------------------------------------------------------ */

export function useRecipes(enabled = true): UseQueryResult<Recipe[]> {
  return useQuery({ queryKey: keys.recipes, queryFn: () => api.listRecipes(), enabled });
}

export function useRecipe(id: string | undefined): UseQueryResult<Recipe> {
  return useQuery({
    queryKey: keys.recipe(id ?? ''),
    queryFn: () => api.getRecipe(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Un fallo de red o del servidor, frente a un problema de datos de la receta.
 *
 * El motor de costeo devuelve sus errores como 400 con el motivo escrito:
 * esos NO se reintentan, porque volver a pedirlo da exactamente lo mismo. Un
 * 500, un 429 o un corte de red si: ahi la receta no tiene nada malo.
 */
export function esFalloPasajero(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === 'NETWORK' || error.code === 'INTERNAL' || error.status >= 500;
  }
  // Sin ApiError no sabemos que ha pasado; tratarlo como pasajero solo cuesta
  // un reintento y evita marcar "Revisar" en una receta sana.
  return true;
}

export function useRecipeCost(id: string | undefined): UseQueryResult<RecipeCost> {
  return useQuery({
    queryKey: keys.recipeCost(id ?? ''),
    queryFn: () => api.getRecipeCost(id as string),
    enabled: Boolean(id),
    retry: (intentos, error) => intentos < 2 && esFalloPasajero(error),
    retryDelay: (intentos) => 400 * 2 ** intentos,
  });
}

/**
 * Coste de todas las recetas en una sola peticion.
 *
 * Sustituye a un `useRecipeCost` por fila en la tabla de Recetas: cien filas
 * eran cien peticiones a la vez, y las que se caian pintaban "Revisar" sin
 * motivo en recetas correctas.
 */
export function useRecipeCosts(): UseQueryResult<RecipeCostsResponse> {
  return useQuery({
    queryKey: keys.recipeCosts,
    queryFn: () => api.getRecipeCosts(),
    retry: (intentos, error) => intentos < 2 && esFalloPasajero(error),
    retryDelay: (intentos) => 400 * 2 ** intentos,
  });
}

export interface SaveRecipeVars {
  id?: string;
  input: RecipeInput;
}

export function useSaveRecipe(): UseMutationResult<Recipe, Error, SaveRecipeVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveRecipeVars) =>
      id ? api.updateRecipe(id, input) : api.createRecipe(input),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useProposeRecipePhoto(): UseMutationResult<RecipeProposalResponse, Error, Blob> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (file: Blob) => api.proposeRecipeFromFile(file), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}

export function useProposeRecipe(): UseMutationResult<RecipeProposalResponse, Error, { text: string; contexto?: ContextoAlta }> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ text, contexto }) => api.proposeRecipe(text, contexto), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}

export function useTranscribeRecipeAudio(): UseMutationResult<
  { text: string },
  Error,
  { audioBase64: string; mimeType: string }
> {
  return useMutation({ mutationFn: (input) => api.transcribeRecipeAudio(input) });
}

export function useConfirmRecipeProposal(override?: (input: ConfirmRecipeProposalInput) => Promise<ConfirmRecipeProposalResponse>): UseMutationResult<
  ConfirmRecipeProposalResponse,
  Error,
  ConfirmRecipeProposalInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (override) return override(input);
      if (!input.draftId) return api.confirmRecipeProposal(input);
      const draft = await api.confirmDraft(input.draftId, { recipe: input });
      if (!draft.result?.recipeId) throw new Error('La receta no se ha confirmado.');
      const recipe = await api.getRecipe(draft.result.recipeId);
      const ingredients = await api.listIngredients();
      return { recipe, newIngredients: ingredients.filter((item) => draft.result?.ingredientIds?.includes(item.ingredientId)) };
    },
    onSuccess: () => { invalidateCosting(qc); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export function useDeleteRecipe(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRecipe(id),
    onSuccess: () => {
      invalidateCosting(qc);
      void qc.invalidateQueries({ queryKey: KITCHEN_LINKS_KEY });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Fase 7 · enlace público de la vista de cocina                        */
/* ------------------------------------------------------------------ */

const KITCHEN_LINKS_KEY = ['kitchen-links'] as const;

/** Los enlaces activos de la cuenta (recipeId → token). Uno por receta como mucho. */
export function useKitchenLinks(enabled = true): UseQueryResult<KitchenLink[]> {
  return useQuery({ queryKey: KITCHEN_LINKS_KEY, queryFn: () => api.listKitchenLinks(), enabled });
}

export type KitchenLinkAction = 'crear' | 'cambiar' | 'desactivar';

export function useKitchenLinkAction(): UseMutationResult<KitchenLink | undefined, Error, { recipeId: string; accion: KitchenLinkAction }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ recipeId, accion }) => {
      if (accion === 'crear') return api.createKitchenLink(recipeId);
      if (accion === 'cambiar') return api.regenerateKitchenLink(recipeId);
      await api.revokeKitchenLink(recipeId);
      return undefined;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KITCHEN_LINKS_KEY });
      // Un enlace desactivado o cambiado no puede seguir abriéndose desde la caché.
      qc.removeQueries({ queryKey: ['public-kitchen'] });
    },
  });
}

/** SIN sesión: la vista de cocina de un enlace público. Un 404 no se reintenta. */
export function usePublicKitchenView(token: string | undefined): UseQueryResult<KitchenShareView> {
  return useQuery({
    queryKey: ['public-kitchen', token ?? ''],
    queryFn: () => api.getPublicKitchenView(token as string),
    enabled: Boolean(token),
    retry: false,
    // Las fotos llegan con URL firmada de 15 min: se pide de nuevo antes.
    staleTime: 10 * 60_000,
  });
}

/* ------------------------------------------------------------------ */
/* Carta y precios                                                     */
/* ------------------------------------------------------------------ */

export function useMenuItems(enabled = true): UseQueryResult<MenuItem[]> {
  return useQuery({ queryKey: keys.menuItems, queryFn: () => api.listMenuItems(), enabled });
}

/** Precios de toda la carta; el menu engineering, con las unidades del local seleccionado. */
/** Con `salesRange` (fase 7), el menu engineering usa las ventas por plato de ese rango. */
export function usePricing(salesRange?: Period): UseQueryResult<PricingResponse> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.pricing(locationId, salesRange),
    queryFn: () => api.getPricing(locationId, salesRange),
  });
}

/* Fase 7: ventas por plato con fechas. */

export function useDishSales(): UseQueryResult<DishSalesPeriod[]> {
  return useQuery({ queryKey: keys.dishSales, queryFn: () => api.listDishSales() });
}

/** Lo que cuelga de una carga de ventas por plato: la carta, el menu engineering y el recuento. */
function invalidateDishSales(qc: QueryClient): void {
  invalidateAll(qc, [['dish-sales'], ['menu-items'], ['pricing'], ['dashboard'], ['kpis'], ['inventory-result'], ['inventory-ingredients']]);
}

export function useSaveDishSales(): UseMutationResult<DishSalesPeriod, Error, Omit<DishSalesInput, 'locationId'>> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (input) => api.saveDishSales({ ...input, locationId: writeLocationId }),
    onSuccess: () => invalidateDishSales(qc),
  });
}

export function usePlatformComparison(
  id: string | undefined,
): UseQueryResult<PricingAnalysis[]> {
  return useQuery({
    queryKey: keys.platforms(id ?? ''),
    queryFn: () => api.getPlatformComparison(id as string),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCostDeviations(): UseQueryResult<CostDeviation[]> {
  return useQuery({ queryKey: keys.deviations, queryFn: () => api.getCostDeviations() });
}

export interface SaveMenuItemVars {
  id?: string;
  input: MenuItemInput;
}

export function useSaveMenuItem(): UseMutationResult<MenuItem, Error, SaveMenuItemVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveMenuItemVars) =>
      id ? api.updateMenuItem(id, input) : api.createMenuItem(input),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useSaveMenuItemUnits(): UseMutationResult<
  MenuItem,
  Error,
  { id: string; unitsSold: number }
> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, unitsSold }) => api.updateMenuItemUnitsSold(id, unitsSold, writeLocationId),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useDeleteMenuItem(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMenuItem(id),
    onSuccess: () => invalidateCosting(qc),
  });
}

/** "Aceptar coste actual": la referencia pasa a ser el coste de hoy. */
export function useAcceptMenuItemCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.acceptMenuItemCost(id),
    // Aceptar un coste cambia la desviacion, el margen y el semaforo: hay que
    // refrescar TODO lo que cuelga del escandallo. Antes solo invalidaba
    // 'menu-items' y una clave inexistente ('menu'), asi que la tabla de
    // Precios y la tarjeta de recosteo se quedaban igual hasta recargar.
    onSuccess: () => invalidateCosting(queryClient),
  });
}

/* ------------------------------------------------------------------ */
/* Ingresos diarios                                                    */
/* ------------------------------------------------------------------ */

export function useRevenues(period: Period, enabled = true): UseQueryResult<DailyRevenue[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.revenues(period, locationId),
    queryFn: () => api.listRevenues(period, locationId),
    enabled,
  });
}

export function useSaveRevenue(): UseMutationResult<DailyRevenue, Error, DailyRevenueInput> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (input: DailyRevenueInput) =>
      api.saveRevenue({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

/**
 * «Tus ventas de antes, ¿las anotabas con o sin IVA?» (19 oct 2026): se
 * pregunta una vez al abrir «Anotar el día» (ver shared/ventasIvaAntes.ts).
 */
export function useIvaAntes(enabled: boolean): UseQueryResult<import('@alexiarest/shared').EstadoIvaAntes> {
  return useQuery({ queryKey: ['iva-antes'], queryFn: () => api.getIvaAntes(), enabled, staleTime: Infinity });
}

export function useAnswerIvaAntes(): UseMutationResult<
  import('@alexiarest/shared').VentasIvaAntes & { corregidas: number },
  Error,
  import('@alexiarest/shared').RespuestaIvaAntes
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (respuesta: import('@alexiarest/shared').RespuestaIvaAntes) => api.answerIvaAntes(respuesta),
    onSuccess: async (r) => {
      qc.setQueryData(['iva-antes'], { preguntar: false, dias: 0, respuesta: r.respuesta });
      // El panel abierto recarga el día con los importes ya sin IVA.
      if (r.corregidas > 0) {
        await qc.invalidateQueries({ queryKey: ['revenues'] });
        invalidateFinance(qc);
      }
    },
  });
}

export function useDeleteRevenue(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (date: string) => api.deleteRevenue(date, writeLocationId),
    onSuccess: () => invalidateFinance(qc),
  });
}

/** Un dia que no ha entrado y por que, para el informe de la importacion. */
export interface ImportRevenueFailure {
  date: string;
  reason: string;
}

export interface ImportRevenuesResult {
  saved: number;
  total: number;
  fallidos: ImportRevenueFailure[];
  /** Local en el que se han guardado, para decirlo en el informe. */
  locationId: string;
}

/** Errores que no son del dia sino de la sesion o la cuenta: seguir no sirve. */
const cortaLaImportacion = (error: unknown): boolean =>
  error instanceof ApiError &&
  ['UNAUTHORIZED', 'FORBIDDEN', 'PLAN_LIMIT', 'NETWORK', 'RATE_LIMITED'].includes(error.code);

const motivoDe = (error: unknown): string => {
  if (!(error instanceof ApiError)) return error instanceof Error ? error.message : 'error desconocido';
  const detalles = error.details
    .map((d) => [d.path, d.message].filter(Boolean).join(': '))
    .filter(Boolean);
  return detalles.length > 0 ? `${error.message} (${detalles.join('; ')})` : error.message;
};

/**
 * Importacion en lote desde el TPV: un upsert por dia, en serie para no
 * saturar la API.
 *
 * Un dia que falla NO para la importacion (Kilo, oct 2026: enero y febrero se
 * quedaban a medias y no se sabia que habia entrado). Se sigue con el resto y
 * se devuelve la lista de dias que no han entrado con su motivo, para
 * ensenarla y reintentar solo esos. Solo se corta si el fallo es de la
 * sesion o de la cuenta (sin permiso, sin red, limite del plan): ahi los
 * dias que quedan fallarian igual. Reintentar es seguro: el upsert es
 * idempotente.
 */
export function useImportRevenues(): UseMutationResult<ImportRevenuesResult, Error, DailyRevenueInput[]> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: async (days: DailyRevenueInput[]) => {
      let saved = 0;
      const fallidos: ImportRevenueFailure[] = [];
      for (const [index, day] of days.entries()) {
        try {
          await api.saveRevenue({ ...day, locationId: writeLocationId, importSource: 'excel' });
          saved++;
        } catch (error) {
          if (cortaLaImportacion(error)) {
            throw new Error(
              `Se guardaron ${saved} de ${days.length} días y la importación se ha parado en el ${day.date}: ${motivoDe(error)}. ` +
                'Puedes volver a importar el mismo archivo: los días ya guardados se sobrescriben igual.',
            );
          }
          fallidos.push({ date: day.date, reason: motivoDe(error) });
          // Si fallan los diez primeros seguidos, el problema no es de un dia.
          if (saved === 0 && index === 9) {
            throw new Error(
              `Los 10 primeros días han fallado (${fallidos[0].reason}). Revisa el mapeo de columnas antes de seguir.`,
            );
          }
        }
      }
      return { saved, total: days.length, fallidos, locationId: writeLocationId };
    },
    onSettled: () => invalidateFinance(qc),
  });
}

/** Un plato de la carta con sus unidades vendidas nuevas. */
export interface UnitsImportItem {
  item: MenuItem;
  units: number;
}

/** Importacion en lote de unidades vendidas (modo "unidades" del importador). */
/**
 * Importa unidades vendidas como UNA carga con fechas (fase 7). Antes iba
 * plato a plato y pisaba la carga anterior sin decir de qué fechas era.
 */
export function useImportUnitsSold(): UseMutationResult<
  number,
  Error,
  { rows: UnitsImportItem[]; range: Period; source?: DishSalesSource }
> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: async ({ rows, range, source }) => {
      await api.saveDishSales({
        start: range.start,
        end: range.end,
        source: source ?? 'excel',
        ...(writeLocationId ? { locationId: writeLocationId } : {}),
        lines: rows.map(({ item, units }) => ({ menuItemId: item.menuItemId, units })),
      });
      return rows.length;
    },
    onSettled: () => invalidateDishSales(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Gastos                                                              */
/* ------------------------------------------------------------------ */

export function useExpenses(period: Period, enabled = true): UseQueryResult<ExpenseEntry[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.expenses(period, locationId),
    queryFn: () => api.listExpenses(period, locationId),
    enabled,
  });
}

export function useReconciliationCandidates(invoiceId: string | null): UseQueryResult<ExpenseEntry[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.reconciliationCandidates(invoiceId ?? 'none', locationId),
    queryFn: () => api.listReconciliationCandidates(invoiceId!, locationId),
    enabled: Boolean(invoiceId),
  });
}

export function usePayables(today: string, enabled = true): UseQueryResult<PayablesResponse> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: keys.payables(today, locationId), queryFn: () => api.getPayables(today, locationId), enabled });
}

export interface SaveExpenseVars {
  id?: string;
  input: ExpenseInput;
}

export function useSaveExpense(override?: (input: ExpenseInput) => Promise<ExpenseEntry>): UseMutationResult<ExpenseEntry, Error, SaveExpenseVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, input }: SaveExpenseVars) =>
      override ? override({ ...input, locationId: input.locationId ?? writeLocationId }) : id
        ? api.updateExpense(id, { ...input, locationId: input.locationId ?? writeLocationId })
        : api.createExpense({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useReconcileExpense(): UseMutationResult<
  ReconciliationResult,
  Error,
  { invoiceId: string; deliveryNoteIds: string[]; marcarFacturaPagada?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, deliveryNoteIds, marcarFacturaPagada }) =>
      api.reconcileExpense(invoiceId, deliveryNoteIds, marcarFacturaPagada),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useUnreconcileExpense(): UseMutationResult<
  { invoiceId: string; deliveryNotes: number },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => api.unreconcileExpense(invoiceId),
    onSuccess: () => invalidateFinance(qc),
  });
}

export interface ImportExpensesVars {
  items: ExpenseImportItem[];
  /** Dar de alta los proveedores que el archivo trae y no existen. */
  createSuppliers: boolean;
  /**
   * Bloque G (14 oct 2026): posiciones de `items` que se guardan aunque
   * parezcan repetidas («Es otra», o van a «Por confirmar» con la duda).
   */
  permitirRepetidos?: number[];
}

/**
 * Importacion de gastos por archivo (7 sep 2026). Primero los proveedores
 * nuevos (para que cada gasto salga ya enlazado), luego los gastos en tandas
 * de 200. Si una tanda falla, se dice cuantos han entrado: volver a importar
 * el mismo archivo los duplicaria, asi que mejor revisar antes.
 */
export interface ImportExpensesResult {
  saved: number;
  /** Filas que ya estaban guardadas y no se han vuelto a crear. */
  repetidos: number;
  /** Proveedores dados de alta: nombre normalizado → id. */
  proveedoresCreados: Map<string, string>;
}

/**
 * Una tanda ha fallado. `desdeFila` es la fila de datos (desde 1, sin la
 * cabecera) del primer gasto que no ha entrado: todo lo anterior ya esta.
 */
export class ImportExpensesError extends Error {
  constructor(message: string, readonly desdeFila: number) {
    super(message);
    this.name = 'ImportExpensesError';
  }
}

export function useImportExpenses(): UseMutationResult<ImportExpensesResult, Error, ImportExpensesVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, createSuppliers, permitirRepetidos }: ImportExpensesVars) => {
      const permitidos = new Set(permitirRepetidos ?? []);
      const created = new Map<string, string>();
      if (createSuppliers) {
        const names = [...new Set(items.map((i) => i.newSupplierName).filter((n): n is string => !!n))];
        for (const name of names) {
          const supplier = await api.createSupplier({ name });
          created.set(normalizeText(name), supplier.supplierId);
        }
      }
      const inputs = items.map((i) => ({
        ...i.input,
        supplierId:
          i.input.supplierId ?? (i.newSupplierName ? created.get(normalizeText(i.newSupplierName)) : undefined),
      }));
      let saved = 0;
      let repetidos = 0;
      // 200 y no 500: cada tanda se crea en serie de 10 en 10 en el servidor, y
      // con 500 una cuenta grande rozaba los 29 s de la pasarela y la tanda se
      // cortaba sin saber que habia entrado (Kilo, oct 2026).
      const TANDA = 200;
      for (let start = 0; start < inputs.length; start += TANDA) {
        try {
          const tanda = inputs.slice(start, start + TANDA);
          const enTanda = tanda.map((_, i) => i).filter((i) => permitidos.has(start + i));
          const { created: n, skipped } = await api.importExpenses(tanda, enTanda.length ? enTanda : undefined);
          saved += n;
          repetidos += skipped;
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'error desconocido';
          throw new ImportExpensesError(
            `Se importaron ${saved} de ${inputs.length} gastos antes de fallar: ${reason}`,
            items[start].row,
          );
        }
      }
      return { saved, repetidos, proveedoresCreados: created };
    },
    onSettled: () => {
      invalidateFinance(qc);
      invalidateAll(qc, [['suppliers']]);
    },
  });
}

/* --- Bloque G (14 oct 2026): Gastos --- */

/** Los gastos «Por confirmar» de cualquier fecha (el aviso de arriba de Gastos). */
export function usePorConfirmar(): UseQueryResult<ExpenseEntry[]> {
  // Bloque C: uno, varios («a,b») o todos los locales marcados arriba.
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: ['expenses', 'por-confirmar', locationId ?? 'all'],
    queryFn: () => api.listPorConfirmar(locationId),
  });
}

/**
 * Notas de un proveedor que esperan factura, de los últimos seis meses: el
 * panel pregunta «¿Esta factura las junta?» antes de guardarla (regla 5).
 */
export function useNotasSinFactura(supplierId: string | undefined, hoy: string): UseQueryResult<ExpenseEntry[]> {
  // Bloque C: uno, varios («a,b») o todos los locales marcados arriba.
  const { ambito: locationId } = useLocationScope();
  const desde = `${addMonths(hoy.slice(0, 7), -6)}-01`;
  return useQuery({
    queryKey: ['expenses', 'notas-sin-factura', locationId ?? 'all', supplierId ?? '', desde, hoy],
    queryFn: async () =>
      (await api.listExpenses({ start: desde, end: hoy }, locationId)).filter(
        (e) => e.documentType === 'albaran' && !e.reconciledInvoiceId && e.supplierId === supplierId,
      ),
    enabled: Boolean(supplierId),
  });
}

/** El último gasto de cada proveedor (del último año): lo que «recuerda» si su ficha aún no guarda nada. */
export function useUltimosGastos(hoy: string): UseQueryResult<Map<string, ExpenseEntry>> {
  // Bloque C: uno, varios («a,b») o todos los locales marcados arriba.
  const { ambito: locationId } = useLocationScope();
  const desde = `${addMonths(hoy.slice(0, 7), -12)}-01`;
  return useQuery({
    queryKey: ['expenses', 'ultimos-por-proveedor', locationId ?? 'all', desde, hoy],
    queryFn: async () => {
      const out = new Map<string, ExpenseEntry>();
      for (const e of await api.listExpenses({ start: desde, end: hoy }, locationId)) {
        if (!e.supplierId) continue;
        const antes = out.get(e.supplierId);
        if (!antes || antes.date < e.date) out.set(e.supplierId, e);
      }
      return out;
    },
    staleTime: 60_000,
  });
}

/* --- Mapeos guardados y cuadre con la fuente (Kilo, oct 2026) --- */

export function useImportMappings(kind: ImportMappingKind): UseQueryResult<SavedImportMapping[]> {
  return useQuery({ queryKey: ['import-mappings', kind], queryFn: () => api.listImportMappings(kind) });
}

/**
 * Recuerda el formato del archivo tras una importacion que ha entrado. Si
 * falla no se dice nada: la importacion ya esta hecha y lo unico que se
 * pierde es el atajo de la proxima vez.
 */
export function useSaveImportMapping(kind: ImportMappingKind): UseMutationResult<SavedImportMapping[], Error, ImportMappingInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportMappingInput) => api.saveImportMapping(kind, input),
    onSuccess: (items) => qc.setQueryData(['import-mappings', kind], items),
  });
}

export function useCuadreFuentes(kind: CuadreKind): UseQueryResult<CuadreFuente[]> {
  return useQuery({ queryKey: ['cuadre', kind], queryFn: () => api.listCuadreFuentes(kind) });
}

export interface CuadreFuenteVars {
  month: string;
  /** undefined = consolidado de todos los locales. */
  locationId?: string;
  /** null = quitar el total apuntado. */
  input: CuadreFuenteInput | null;
}

export function useSaveCuadreFuente(kind: CuadreKind): UseMutationResult<void, Error, CuadreFuenteVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ month, locationId, input }: CuadreFuenteVars) => {
      if (input) await api.saveCuadreFuente(kind, month, locationId, input);
      else await api.deleteCuadreFuente(kind, month, locationId);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['cuadre', kind] }),
  });
}

/* --- Escaner de facturas --- */

export function useScanQuota(): UseQueryResult<ScanQuotaResponse> {
  return useQuery({ queryKey: ['scan-quota'], queryFn: () => api.getScanQuota() });
}

/**
 * Sube la foto y la manda a leer. Devuelve la propuesta de gasto; NO crea
 * nada: guardarlo es un `useSaveExpense` aparte, con lo que el usuario haya
 * corregido en la pantalla.
 */
/** Sube el documento al S3 del escaner y devuelve su clave. */
async function subirDocumento(blob: Blob): Promise<string> {
  const contentType = blob.type || 'image/jpeg';
  const { key, uploadUrl, fields } = await api.createScanUploadUrl(contentType);
  const body = new FormData();
  for (const [name, value] of Object.entries(fields)) body.append(name, value);
  body.append('file', blob);
  const res = await fetch(uploadUrl, { method: 'POST', body });
  if (!res.ok) throw new Error('No hemos podido subir el documento. Inténtalo otra vez.');
  return key;
}

/**
 * Hoja de recuento rellenada a mano -> cantidades propuestas (mejora 40).
 * NO guarda el recuento: eso lo hace `useSaveInventoryCount` con lo que el
 * usuario haya revisado en pantalla.
 */
export function useScanInventorySheet(): UseMutationResult<InventoryScanResult, Error, Blob> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blob: Blob) => {
      if (isDemoMode) return api.scanInventorySheet('demo');
      return api.scanInventorySheet(await subirDocumento(blob));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['scan-quota'] }); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

/** Foto del corte de caja → campos del día. No guarda: los guarda el panel del día. */
export function useScanCorte(): UseMutationResult<RevenueFill, Error, Blob> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blob: Blob) => api.scanCorte(isDemoMode ? 'demo' : await subirDocumento(blob)),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['scan-quota'] }); },
  });
}

/** Lo dictado → campos del día. */
export function useFillRevenueForm(): UseMutationResult<RevenueFill, Error, string> {
  return useMutation({ mutationFn: (text: string) => api.fillRevenueForm(text) });
}

export interface ScanVars {
  blob: Blob;
  /** Que se escanea: el aviso de duplicado se busca entre documentos del mismo tipo. */
  documentType?: PurchaseDocumentType;
}

export function useScanReceipt(): UseMutationResult<ScanResult, Error, ScanVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blob, documentType }: ScanVars) => {
      // En la demo no hay S3 ni OCR: se salta la subida y el adaptador
      // devuelve una factura de ejemplo. Asi el escaparate recorre la MISMA
      // pantalla y el mismo motor de reglas que la app real.
      // (un PDF en la demo es la factura de ejemplo de dos paginas)
      if (isDemoMode) return api.scanReceipt(blob.type === 'application/pdf' ? 'demo-2-paginas' : 'demo', documentType);

      const contentType = blob.type || 'image/jpeg';
      const { key, uploadUrl, fields } = await api.createScanUploadUrl(contentType);
      const body = new FormData();
      for (const [name, value] of Object.entries(fields)) body.append(name, value);
      body.append('file', blob);
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body,
      });
      if (!res.ok) throw new Error('No hemos podido subir el documento. Inténtalo otra vez.');
      return api.scanReceipt(key, documentType);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['scan-quota'] }); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export function useDeleteExpense(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteExpense(id),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Inversiones                                                         */
/* ------------------------------------------------------------------ */

export function useInvestments(): UseQueryResult<Investment[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: keys.investments(locationId), queryFn: () => api.listInvestments(locationId) });
}

export function useAmortizationSchedule(month: string): UseQueryResult<AmortizationRow[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.amortization(month, locationId),
    queryFn: () => api.getAmortizationSchedule(month, locationId),
  });
}

export interface SaveInvestmentVars {
  id?: string;
  input: InvestmentInput;
}

export function useSaveInvestment(): UseMutationResult<Investment, Error, SaveInvestmentVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, input }: SaveInvestmentVars) =>
      id
        ? api.updateInvestment(id, { ...input, locationId: input.locationId ?? writeLocationId })
        : api.createInvestment({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteInvestment(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteInvestment(id),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Bloque I: préstamos, gastos que parecen inversiones, objetivos       */
/* ------------------------------------------------------------------ */

export function useLoans(): UseQueryResult<import('./contract').LoansResponse> {
  // Bloque C: uno, varios («a,b») o todos los locales marcados arriba.
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: ['loans', locationId ?? 'all'], queryFn: () => api.listLoans(locationId) });
}

export interface SaveLoanVars {
  id?: string;
  input: import('@alexiarest/shared').LoanInput;
}

export function useSaveLoan(): UseMutationResult<import('@alexiarest/shared').Loan, Error, SaveLoanVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, input }: SaveLoanVars) =>
      id
        ? api.updateLoan(id, { ...input, locationId: input.locationId ?? writeLocationId })
        : api.createLoan({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteLoan(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.deleteLoan(id), onSuccess: () => invalidateFinance(qc) });
}

export function useLooksLikeInvestments(): UseQueryResult<import('@alexiarest/shared').GastoQueParece[]> {
  // Bloque C: uno, varios («a,b») o todos los locales marcados arriba.
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: ['looks-like-investments', locationId ?? 'all'], queryFn: () => api.listLooksLikeInvestments(locationId) });
}

export function useInvestmentFromExpense(): UseMutationResult<Investment, Error, string> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (entryId: string) => api.investmentFromExpense(entryId), onSuccess: () => invalidateFinance(qc) });
}

/** Objetivos para siempre de un local o de todos (`grupo`); sin nada, el que toque. */
export function useStandingTarget(locationId?: string): UseQueryResult<import('./contract').StandingTargetResponse> {
  return useQuery({ queryKey: ['standing-target', locationId ?? 'auto'], queryFn: () => api.getStandingTarget(locationId) });
}

export function useSaveStandingTarget(): UseMutationResult<Target, Error, import('@alexiarest/shared').StandingTargetInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.saveStandingTarget(input),
    onSuccess: (target, input) => {
      // La pantalla ya tiene lo que escribió: lo guardado se pone en la caché
      // de ese local (sin volver a pedirlo, así no se rehace el formulario) y
      // lo demás se vuelve a pedir: la casilla «Los mismos para todos», los
      // otros locales (al volver a uno no salen valores viejos) y lo que lo usa.
      const guardadoEn = input.locationId === SHARED_LOCATION_ID ? SHARED_LOCATION_ID : effectiveLocationId(input.locationId);
      const esEse = (data: unknown) => (data as StandingTargetResponse | undefined)?.locationId === guardadoEn;
      qc.setQueriesData<StandingTargetResponse>({ queryKey: ['standing-target'] }, (old) =>
        old && old.locationId === guardadoEn
          ? { ...old, target, ...(input.sameForAllLocations !== undefined ? { sameForAllLocations: input.sameForAllLocations } : {}) }
          : old,
      );
      qc.removeQueries({ queryKey: ['standing-target'], type: 'inactive', predicate: (q) => !esEse(q.state.data) });
      void qc.invalidateQueries({ queryKey: ['standing-target'], type: 'active', predicate: (q) => !esEse(q.state.data) });
      for (const key of [['target'], ['target-comparison'], ['dashboard'], ['pnl'], ['pnl-monthly'], ['kpis']]) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Movimientos de caja                                                 */
/* ------------------------------------------------------------------ */

export function useCashMovements(period: Period): UseQueryResult<CashMovement[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.cashMovements(period, locationId),
    queryFn: () => api.listCashMovements(period, locationId),
  });
}

export function useSaveCashMovement(): UseMutationResult<
  CashMovement,
  Error,
  CashMovementInput
> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (input: CashMovementInput) => api.createCashMovement({ ...input, locationId: writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Objetivos                                                           */
/* ------------------------------------------------------------------ */

/**
 * Objetivo del mes del local seleccionado. Se EDITA por local, asi que en
 * consolidado se lee el del que escribiria (principal): la pantalla no
 * ensenya el formulario en ese caso.
 */
export function useTarget(month: string): UseQueryResult<Target | null> {
  const { locationId, writeLocationId } = useLocationScope();
  const efectivo = locationId ?? writeLocationId;
  return useQuery({
    queryKey: keys.target(month, efectivo),
    queryFn: () => api.getTarget(month, efectivo),
  });
}

/** Objetivo frente a real: por local, o consolidado con «Todos los locales». */
export function useTargetComparison(month: string): UseQueryResult<TargetComparison[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.targetComparison(month, locationId),
    queryFn: () => api.getTargetComparison(month, locationId),
  });
}

/* ------------------------------------------------------------------ */
/* Inventario                                                          */
/* ------------------------------------------------------------------ */

export function useInventoryCounts(): UseQueryResult<InventoryCount[]> {
  return useQuery({ queryKey: keys.inventory, queryFn: () => api.listInventoryCounts() });
}

export function useInventoryCount(month: string): UseQueryResult<InventoryCount> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.inventoryMonth(month, locationId),
    queryFn: () => api.getInventoryCount(month, locationId),
  });
}

/** Fase 7: gasto real frente al de tus recetas. `locationId: null` = todos los locales. */
export function useInventoryResult(
  month: string,
  target: { locationId: string | null; kind?: InventoryCountKind; date?: string },
  enabled = true,
): UseQueryResult<InventoryResult> {
  return useQuery({
    queryKey: keys.inventoryResult(month, target.locationId, target.kind, target.date),
    queryFn: () => api.getInventoryResult(month, target),
    enabled,
  });
}

export function useInventoryIngredients(): UseQueryResult<InventoryIngredientsByLocation> {
  return useQuery({ queryKey: keys.inventoryIngredients, queryFn: () => api.getInventoryIngredients() });
}

export function useSetInventoryIngredientUsed(): UseMutationResult<
  void,
  Error,
  { ingredientId: string; locationId?: string; used: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ingredientId, locationId, used }) => api.setInventoryIngredientUsed(ingredientId, locationId, used),
    onSuccess: () => invalidateAll(qc, [['inventory-ingredients'], ['inventory-result']]),
  });
}

export interface SaveInventoryVars {
  month: string;
  input: InventoryCountInput;
  /** Guardado automático mientras se cuenta: solo refresca el recuento. */
  ligero?: boolean;
}

export function useSaveInventoryCount(): UseMutationResult<
  InventoryCount,
  Error,
  SaveInventoryVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, input }: SaveInventoryVars) => api.saveInventoryCount(month, input),
    // Fase 7: mientras se cuenta se guarda solo cada pocos segundos; un
    // recuento abierto no entra en la P&L, así que no hace falta refrescarla.
    onSuccess: (_saved, vars) =>
      vars.ligero
        ? invalidateAll(qc, [['inventory'], ['inventory-month'], ['inventory-result']])
        : invalidateFinance(qc),
  });
}

export function useDeleteInventoryCount(): UseMutationResult<
  void,
  Error,
  { month: string; locationId?: string; kind?: InventoryCountKind; date?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, locationId, kind, date }) => api.deleteInventoryCount(month, locationId, kind, date),
    onSuccess: () => invalidateFinance(qc),
  });
}

export interface SaveTargetVars {
  month: string;
  input: TargetInput;
}

export function useSaveTarget(): UseMutationResult<Target, Error, SaveTargetVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ month, input }: SaveTargetVars) =>
      api.saveTarget(month, { ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Analitica                                                           */
/* ------------------------------------------------------------------ */

export function useDashboard(period: Period): UseQueryResult<DashboardResponse> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.dashboard(period, locationId),
    queryFn: () => api.getDashboard(period, locationId),
  });
}

export function usePnl(period: Period): UseQueryResult<ProfitAndLoss> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: keys.pnl(period, locationId), queryFn: () => api.getPnl(period, locationId) });
}

export function useMonthlyPnl(period: Period): UseQueryResult<ProfitAndLoss[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.pnlMonthly(period, locationId),
    queryFn: () => api.getMonthlyPnl(period, locationId),
  });
}

export function useCashFlow(period: Period): UseQueryResult<CashFlowResponse> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.cashflow(period, locationId),
    queryFn: () => api.getCashFlow(period, 0, locationId),
  });
}

export function useKpis(period: Period): UseQueryResult<KpisResponse> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: keys.kpis(period, locationId), queryFn: () => api.getKpis(period, locationId) });
}

export function useWeekday(period: Period): UseQueryResult<WeekdayPerformance[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({ queryKey: keys.weekday(period, locationId), queryFn: () => api.getWeekday(period, locationId) });
}

export function useTaxAllocation(): UseMutationResult<
  TaxAllocation[],
  Error,
  TaxAllocationRequest
> {
  return useMutation({ mutationFn: (input: TaxAllocationRequest) => api.allocateTax(input) });
}

/* ------------------------------------------------------------------ */
/* Asistente de IA                                                     */
/* ------------------------------------------------------------------ */

/**
 * Preguntas sugeridas del periodo. No se reintenta: si el asistente no esta
 * disponible, la pantalla lo dice una vez y no insiste.
 */
export function useAssistantSuggestions(
  period: Period,
  /** false = este usuario no tiene asistente: no se pide nada. */
  enabled = true,
): UseQueryResult<AssistantSuggestion[]> {
  const { ambito: locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.assistantSuggestions(period, locationId),
    queryFn: () => api.getAssistantSuggestions(period, locationId),
    retry: false,
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useAssistantChat(): UseMutationResult<
  AssistantChatResponse,
  Error,
  AssistantChatRequest
> {
  return useMutation({
    mutationFn: (input: AssistantChatRequest) => api.chatWithAssistant(input),
    retry: false,
  });
}

export interface MapeoVentasVars {
  columnas: string[];
  muestra: string[][];
  destinos: Array<{ key: string; label: string }>;
  /** Ventas del TPV (por defecto) o gastos. */
  tipo?: 'ventas' | 'gastos';
  /** La hoja tal cual: Alexia dice también dónde está la tabla. */
  rejilla?: RejillaArchivo;
}

/**
 * Le pide a Alexia que diga que es cada columna del archivo que se acaba de
 * subir. Es una propuesta: se pinta en el mapeo y el usuario la corrige.
 */
export function useMapeoVentas(): UseMutationResult<
  MapeoVentasPropuesta,
  Error,
  MapeoVentasVars
> {
  return useMutation({
    mutationFn: (input: MapeoVentasVars) => api.proponerMapeoVentas(input),
    retry: false,
  });
}

/* ------------------------------------------------------------------ */
/* Facturacion                                                         */
/* ------------------------------------------------------------------ */

/**
 * Facturas de la suscripcion. `staleTime` alto: la lista cambia una vez al mes
 * y cada consulta es una llamada a Holded.
 */
export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.getInvoices(),
    staleTime: 5 * 60_000,
  });
}

/** Plan y periodo de cobro: mensual (oferta de lanzamiento) o anual (9 meses por 12). */
export interface PlanChoice {
  plan: 'basic' | 'pro';
  interval?: BillingInterval;
}

export function useCheckout(): UseMutationResult<{ url: string }, Error, PlanChoice & { tax?: TaxProfile; returnTo?: '/ajustes' | '/elegir-plan' }> {
  return useMutation({
    mutationFn: ({ plan, interval, tax, returnTo }: PlanChoice & { tax?: TaxProfile; returnTo?: '/ajustes' | '/elegir-plan' }) =>
      api.startCheckout(plan, interval ?? 'month', tax || returnTo ? { tax, returnTo } : undefined),
  });
}

/** Fin de la prueba (sección 18): lo que consiguió y el plan recomendado. Solo dueño o admin. */
export function useTrialSummary(enabled = true): UseQueryResult<ResumenPrueba> {
  return useQuery({ queryKey: ['trial-summary'], queryFn: () => api.getTrialSummary(), enabled, staleTime: 5 * 60_000, retry: false });
}

/** «Comprar 150 escaneos»: abre el pago único de Stripe. Los escaneos los suma el webhook. */
export function useScanPackCheckout(): UseMutationResult<{ url: string }, Error, void> {
  return useMutation({ mutationFn: () => api.startScanPackCheckout() });
}

export function useFounderCheckout(): UseMutationResult<{ url: string }, Error, string> {
  return useMutation({ mutationFn: (code: string) => api.startFounderCheckout(code) });
}

export function useChangePlan(): UseMutationResult<{ ok: true }, Error, PlanChoice> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ plan, interval }: PlanChoice) => api.changePlan(plan, interval),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      // El plan lo fija el webhook y puede tardar un par de segundos mas que
      // nuestra respuesta: segunda pasada para que la pantalla lo recoja.
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['me'] }), 4000);
    },
  });
}

export function useBillingPortal(): UseMutationResult<{ url: string }, Error, void> {
  return useMutation({ mutationFn: () => api.openBillingPortal() });
}

/** «Reanudar ahora» (retención, 18 oct 2026): quita la pausa y relee la cuenta. */
export function useResumePlan(): UseMutationResult<{ ok: true }, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resumePlan(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['me'] }), 4000);
    },
  });
}

/** Fija la cantidad de asientos extra. `extraSeats` lo escribe el webhook. */
export function useSetSeats(): UseMutationResult<{ ok: true }, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quantity: number) => api.setExtraSeats(quantity),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.team });
      // El webhook puede tardar un par de segundos mas que nuestra respuesta.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.team });
      }, 4000);
    },
  });
}

/** Fija la cantidad de centros extra. `extraLocations` lo escribe el webhook. */
export function useSetExtraLocations(): UseMutationResult<{ ok: true }, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quantity: number) => api.setExtraLocations(quantity),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.locations });
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.locations });
      }, 4000);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Equipo                                                              */
/* ------------------------------------------------------------------ */

export function useTeam(enabled = true): UseQueryResult<TeamResponse> {
  return useQuery({ queryKey: keys.team, queryFn: () => api.getTeam(), enabled });
}

function invalidateTeam(qc: QueryClient): () => void {
  return () => {
    void qc.invalidateQueries({ queryKey: keys.team });
    void qc.invalidateQueries({ queryKey: keys.me });
  };
}

export function useInviteMember(): UseMutationResult<
  { membership: Membership },
  Error,
  InviteInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => api.inviteMember(input),
    onSuccess: invalidateTeam(qc),
  });
}

export function useChangeMemberRole(): UseMutationResult<
  { membership: Membership },
  Error,
  { userId: string; role: AssignableRole; customRoleId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role, customRoleId }) =>
      api.changeMemberRole(userId, role, customRoleId),
    onSuccess: invalidateTeam(qc),
  });
}

/* --- Roles personalizados (mejora 70) --- */

export function useCustomRoles(enabled = true): UseQueryResult<CustomRole[]> {
  return useQuery({
    queryKey: keys.customRoles,
    queryFn: async () => (await api.listCustomRoles()).roles,
    enabled,
  });
}

function invalidateRoles(qc: QueryClient): () => void {
  return () => {
    void qc.invalidateQueries({ queryKey: keys.customRoles });
    // Cambiar un rol cambia lo que ve quien lo lleva, empezando por uno mismo.
    void qc.invalidateQueries({ queryKey: keys.team });
    void qc.invalidateQueries({ queryKey: keys.me });
  };
}

export function useSaveCustomRole(): UseMutationResult<
  { role: CustomRole },
  Error,
  { roleId?: string; input: CustomRoleInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, input }) =>
      roleId ? api.updateCustomRole(roleId, input) : api.createCustomRole(input),
    onSuccess: invalidateRoles(qc),
  });
}

export function useDeleteCustomRole(): UseMutationResult<{ ok: true }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => api.deleteCustomRole(roleId),
    onSuccess: invalidateRoles(qc),
  });
}

export function useMemberStatus(): UseMutationResult<
  { membership: Membership },
  Error,
  { userId: string; accion: 'desactivar' | 'reactivar' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, accion }) =>
      accion === 'desactivar' ? api.deactivateMember(userId) : api.activateMember(userId),
    onSuccess: invalidateTeam(qc),
  });
}

/** Reenvia la invitacion (nueva contraseña temporal) a quien aun no ha entrado. */
export function useResendInvitation(): UseMutationResult<
  { ok: true; membership: Membership; aviso?: string },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.resendInvitation(userId),
    onSuccess: invalidateTeam(qc),
  });
}

/** Contrata o da de baja el modulo escaner. El addon lo fija el webhook. */
export function useAddon(): UseMutationResult<
  { ok: true },
  Error,
  { addon: 'escaner'; accion: 'contratar' | 'cancelar' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addon, accion }) =>
      accion === 'contratar' ? api.contractAddon(addon) : api.cancelAddon(addon),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['receipts'] });
      // Igual que el plan: lo fija el webhook con un par de segundos de margen.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['me'] });
        void qc.invalidateQueries({ queryKey: ['receipts'] });
      }, 4000);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Importacion masiva del catalogo                                     */
/* ------------------------------------------------------------------ */

/**
 * Sube el catalogo en tandas: la API acepta 200 por peticion, y un fichero
 * de 600 ingredientes no puede fallar entero por eso.
 */
const TAMANYO_TANDA = 200;

export function useImportIngredients(override?: (batch: IngredientBatch) => Promise<BatchResult>): UseMutationResult<BatchResult, Error, IngredientBatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batch: IngredientBatch) => {
      if (override) return override(batch);
      const total: BatchResult = { creados: 0, actualizados: 0, errores: [] };
      for (let i = 0; i < batch.items.length; i += TAMANYO_TANDA) {
        const parcial = await api.importIngredients({
          // Los proveedores solo en la primera tanda: ya quedan creados.
          suppliers: i === 0 ? batch.suppliers : undefined,
          items: batch.items.slice(i, i + TAMANYO_TANDA),
        });
        total.creados += parcial.creados;
        total.actualizados += parcial.actualizados;
        total.errores.push(
          ...parcial.errores.map((e) => ({ ...e, index: e.index + i })),
        );
      }
      return total;
    },
    onSuccess: () => {
      invalidateCosting(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

/** Una receta del lote con su posicion en el fichero, para no perderla. */
interface RecetaEnTanda {
  item: RecipeBatch['items'][number];
  indice: number;
}

/**
 * Sube recetas en tandas de 200 y devuelve las que han fallado, con su
 * posicion original.
 */
async function enviarRecetas(lote: RecetaEnTanda[]): Promise<{
  creados: number;
  actualizados: number;
  fallidos: Array<RecetaEnTanda & { name: string; message: string }>;
}> {
  const out = {
    creados: 0,
    actualizados: 0,
    fallidos: [] as Array<RecetaEnTanda & { name: string; message: string }>,
  };
  for (let i = 0; i < lote.length; i += TAMANYO_TANDA) {
    const trozo = lote.slice(i, i + TAMANYO_TANDA);
    const parcial = await api.importRecipes({ items: trozo.map((t) => t.item) });
    out.creados += parcial.creados;
    out.actualizados += parcial.actualizados;
    for (const e of parcial.errores) {
      const origen = trozo[e.index];
      if (origen) out.fallidos.push({ ...origen, name: e.name, message: e.message });
    }
  }
  return out;
}

export function useImportRecipes(): UseMutationResult<BatchResult, Error, RecipeBatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batch: RecipeBatch) => {
      const todas: RecetaEnTanda[] = batch.items.map((item, indice) => ({ item, indice }));
      const primera = await enviarRecetas(todas);
      const total: BatchResult = {
        creados: primera.creados,
        actualizados: primera.actualizados,
        errores: [],
      };

      /*
       * Segunda vuelta con las que han fallado.
       *
       * El fichero se parte en tandas de 200, y una receta puede apuntar a una
       * subreceta que caia en una tanda POSTERIOR: cuando se intento, esa
       * subreceta todavia no existia. El servidor no la deja a medias —no
       * escribe nada si no puede enlazarlo todo—, asi que reintentarla cuando
       * ya esta el fichero entero es seguro y no duplica nada.
       *
       * Solo si hubo mas de una tanda: dentro de una, el servidor ya resuelve
       * el orden el solo y reintentar seria pedirle lo mismo dos veces.
       */
      let fallidos = primera.fallidos;
      if (fallidos.length > 0 && todas.length > TAMANYO_TANDA) {
        const segunda = await enviarRecetas(fallidos.map(({ item, indice }) => ({ item, indice })));
        total.creados += segunda.creados;
        total.actualizados += segunda.actualizados;
        fallidos = segunda.fallidos;
      }

      total.errores = fallidos
        .map((f) => ({ index: f.indice, name: f.name, message: f.message }))
        .sort((a, b) => a.index - b.index);
      return total;
    },
    onSuccess: () => invalidateCosting(queryClient),
  });
}

/* ------------------------------------------------------------------ */
/* Panel de plataforma (solo administradores)                          */
/* ------------------------------------------------------------------ */

export function useAdminOverview(): UseQueryResult<AdminOverview> {
  return useQuery({
    queryKey: keys.adminOverview,
    queryFn: () => api.getAdminOverview(),
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * El servidor sirve el overview de una cache que el scheduler refresca cada
 * hora: este es el boton «Actualizar» que la salta y recalcula ahora.
 */
export function useAdminRefreshOverview(): UseMutationResult<AdminOverview, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.getAdminOverview(true),
    onSuccess: (data) => qc.setQueryData(keys.adminOverview, data),
  });
}

export function useAdminChangePlan(): UseMutationResult<
  { ok: true },
  Error,
  { tenantId: string; plan: 'basic' | 'pro' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, plan }) => api.adminChangePlan(tenantId, plan),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      // El plan lo fija el webhook; segunda pasada cuando haya llegado.
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.adminOverview }), 4000);
    },
  });
}

export function useAdminApplyCoupon(): UseMutationResult<
  { ok: true },
  Error,
  { tenantId: string; coupon: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, coupon }) => api.adminApplyCoupon(tenantId, coupon),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

const invalidaPnl = (qc: ReturnType<typeof useQueryClient>) =>
  void qc.invalidateQueries({ queryKey: ['admin-pnl'] });

export function useAdminEnviarResumenSemanal(): UseMutationResult<
  { enviado: boolean; destinatarios: string[]; asunto: string },
  Error,
  void
> {
  return useMutation({ mutationFn: () => api.adminEnviarResumenSemanal() });
}

/** Correo «Alexia tiene nueva cara» (18 oct 2026): vista previa y estado del envío. */
export function useAdminCampanaNovedades(enabled = true): UseQueryResult<CampanaNovedadesEstado> {
  return useQuery({ queryKey: ['admin-campana-novedades'], queryFn: () => api.adminCampanaNovedades(), enabled });
}

export function useAdminPruebaNovedades(): UseMutationResult<{ enviadoA: string; asunto: string }, Error, void> {
  return useMutation({ mutationFn: () => api.adminPruebaNovedades() });
}

export function useAdminRetryHolded(): UseMutationResult<
  { resultados: HoldedReintento[] },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId) => api.adminRetryHolded(tenantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

export function useAdminMarkAssistedOnboarding(): UseMutationResult<
  { ok: true; days: string[]; activationReset: boolean },
  Error,
  { tenantId: string; day: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, day }) => api.adminMarkAssistedOnboarding(tenantId, day),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

export function useAdminSetInternal(): UseMutationResult<
  { ok: true; internal: boolean },
  Error,
  { tenantId: string; internal: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, internal }) => api.adminSetInternal(tenantId, internal),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      invalidaPnl(qc);
    },
  });
}

export function useAdminSetCortesia(): UseMutationResult<
  { ok: true; cortesia: boolean },
  Error,
  { tenantId: string; cortesia: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, cortesia }) => api.adminSetCortesia(tenantId, cortesia),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      invalidaPnl(qc);
    },
  });
}

export function useAdminSetBonusLocations(): UseMutationResult<
  { ok: true; bonusLocations: number },
  Error,
  { tenantId: string; bonusLocations: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, bonusLocations }) => api.adminSetBonusLocations(tenantId, bonusLocations),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

export function usePlatformPnl(year: number): UseQueryResult<PlatformPnlResponse> {
  return useQuery({
    queryKey: keys.adminPnl(year),
    queryFn: () => api.getPlatformPnl(year),
    retry: false,
    staleTime: 60_000,
  });
}

export function useCreatePlatformExpense(): UseMutationResult<
  PlatformExpense,
  Error,
  PlatformExpenseInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createPlatformExpense(input),
    onSuccess: (_, input) =>
      void qc.invalidateQueries({ queryKey: keys.adminPnl(Number(input.month.slice(0, 4))) }),
  });
}

export function useCreatePlatformRecurring(): UseMutationResult<
  PlatformRecurringExpense,
  Error,
  PlatformRecurringExpenseInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createPlatformRecurring(input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useDeletePlatformRecurring(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.deletePlatformRecurring(id),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useSetPlatformSettings(): UseMutationResult<PlatformSettings, Error, PlatformSettings> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.setPlatformSettings(input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useSetPlatformSettlement(): UseMutationResult<
  unknown,
  Error,
  { month: string; done: boolean; note?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, ...input }) => api.setPlatformSettlement(month, input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useDeletePlatformExpense(): UseMutationResult<
  void,
  Error,
  { month: string; expenseId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, expenseId }) => api.deletePlatformExpense(month, expenseId),
    onSuccess: (_, { month }) =>
      void qc.invalidateQueries({ queryKey: keys.adminPnl(Number(month.slice(0, 4))) }),
  });
}

/* ------------------------------------------------------------------ */
/* Integraciones con TPV                                               */
/* ------------------------------------------------------------------ */

export function usePosIntegrations(enabled = true): UseQueryResult<PosIntegrationsResponse> {
  return useQuery({ queryKey: keys.posIntegrations, queryFn: () => api.listPosIntegrations(), enabled });
}

/** `locationId`: el local desde el que se conecta (cada local, su punto de venta). */
export function useStartPosConnection(locationId?: string): UseMutationResult<{ url: string }, Error, PosProvider> {
  return useMutation({ mutationFn: (provider) => api.startPosConnection(provider, locationId) });
}

export function useCompletePosConnection(): UseMutationResult<
  PosIntegrationView,
  Error,
  { provider: PosProvider; code: string; state: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, code, state }) => api.completePosConnection(provider, { code, state }),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useConnectLoyverseToken(locationId?: string): UseMutationResult<PosIntegrationView, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token) => api.connectLoyverseToken(token, locationId),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useConnectGlopCredentials(locationId?: string): UseMutationResult<
  PosIntegrationView,
  Error,
  { clientId: string; clientSecret: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.connectGlopCredentials(input, locationId),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useConnectLastAppAccount(locationId?: string): UseMutationResult<PosIntegrationView, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.connectLastAppAccount(locationId),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useUpdatePosIntegration(): UseMutationResult<
  PosIntegrationView,
  Error,
  { provider: PosProvider; connectionId: string; patch: PosIntegrationPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, connectionId, patch }) => api.updatePosIntegration(provider, connectionId, patch),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

/** Trae las ventas del rango al libro de ingresos. */
/** Parte un rango en tramos que no cruzan de mes. */
export function tramosPorMes(period: Period): Period[] {
  const tramos: Period[] = [];
  let start = period.start;
  while (start <= period.end) {
    const [y, m] = start.split('-').map(Number);
    const finDeMes = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    const end = finDeMes < period.end ? finDeMes : period.end;
    tramos.push({ start, end });
    start = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  }
  return tramos;
}

function sumarSync(a: PosSyncResult, b: PosSyncResult): PosSyncResult {
  const saltadas = (a.skippedSalesCount ?? 0) + (b.skippedSalesCount ?? 0);
  return {
    ...a,
    receipts: a.receipts + b.receipts,
    days: a.days + b.days,
    skippedStores: [...new Set([...a.skippedStores, ...b.skippedStores])],
    ...(saltadas
      ? { skippedSalesCount: saltadas, skippedSales: [...(a.skippedSales ?? []), ...(b.skippedSales ?? [])].slice(0, 20) }
      : {}),
  };
}

export function useSyncPosSales(): UseMutationResult<
  PosSyncResult,
  Error,
  { provider: PosProvider; connectionId: string; period: Period }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ provider, connectionId, period }) => {
      /*
       * Por meses naturales y en serie (Kilo, oct 2026): tres meses de varios
       * locales en una sola llamada se pasaban del plazo de la pasarela y no
       * entraba nada. Un rango dentro de un mes es una sola llamada, como antes.
       */
      const tramos = tramosPorMes(period);
      let total: PosSyncResult | null = null;
      for (const [i, tramo] of tramos.entries()) {
        let parcial: PosSyncResult;
        try {
          parcial = await api.syncPosSales(provider, connectionId, tramo);
        } catch (error) {
          if (i === 0) throw error;
          const reason = error instanceof Error ? error.message : 'error desconocido';
          throw new Error(`Se trajeron las ventas hasta el ${tramos[i - 1].end}; falló desde el ${tramo.start}: ${reason} Vuelve a traer desde esa fecha.`);
        }
        total = total ? sumarSync(total, parcial) : parcial;
      }
      return { ...total!, start: period.start, end: period.end };
    },
    onSettled: () => {
      invalidateFinance(qc);
      invalidateAll(qc, [['pos-integrations'], ['dashboard'], ['kpis'], ['weekday']]);
    },
  });
}

/** Vuelca las unidades vendidas del periodo a la carta (menu engineering). */
export function useApplyPosUnits(): UseMutationResult<
  PosUnitsResult,
  Error,
  { provider: PosProvider; period: Period }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, period }) => api.applyPosUnits(provider, period),
    onSettled: () => invalidateAll(qc, [['menu-items'], ['pricing'], ['dashboard'], ['kpis']]),
  });
}

export function useDisconnectPos(): UseMutationResult<
  void,
  Error,
  { provider: PosProvider; connectionId: string; locationId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, connectionId, locationId }) => api.disconnectPos(provider, connectionId, locationId),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useEstimateMissingPrices() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.estimateMissingPrices(), onSuccess: () => invalidateCosting(qc) });
}

export function useDrafts(enabled = true) {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: ['drafts', 'list', locationId], queryFn: () => api.listDrafts(locationId), enabled });
}
export function useDraft(id: string) {
  return useQuery({ queryKey: ['drafts', id], queryFn: () => api.getDraft(id), enabled: Boolean(id) });
}
export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.discardDraft(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}

export function useConnectRestaurantPeCredentials(locationId?: string): UseMutationResult<PosIntegrationView, Error, { domainId: string; token: string }> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input) => api.connectRestaurantPeCredentials(input, locationId), onSuccess: () => invalidateAll(qc, [['pos-integrations']]) });
}

export function useConnectPosCredentials(locationId?: string): UseMutationResult<PosIntegrationView, Error, { provider: 'bistrosoft' | 'polotab' | 'sumup'; input: Record<string, string> }> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: ({ provider, input }) => api.connectPosCredentials(provider, input, locationId), onSuccess: () => invalidateAll(qc, [['pos-integrations']]) });
}

/* ------------------------------------------------------------------ */
/* Bloque S · escáner único: «Por revisar», «Guardar las N», «Deshacer» */
/* ------------------------------------------------------------------ */

export function usePorRevisar(enabled = true) {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: ['drafts', 'por-revisar', locationId], queryFn: () => api.listPorRevisar(locationId), enabled });
}

export function useGuardarTodoClaro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.guardarTodoClaro(ids),
    onSuccess: () => { invalidateFinance(qc); invalidateCosting(qc); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export function useDeshacerSinRevisar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expenseId: string) => api.deshacerSinRevisar(expenseId),
    onSuccess: () => { invalidateFinance(qc); invalidateCosting(qc); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}
/* Bloque C (Configuración y varios locales, 14 oct 2026)              */
/* ------------------------------------------------------------------ */

/**
 * Ventas de TODOS los locales que ve la persona, sin mirar el selector: el
 * número del menú («Ventas · 3») cuenta los días sin anotar de todos.
 */
export function useRevenuesDeTodos(period: Period, enabled = true): UseQueryResult<DailyRevenue[]> {
  return useQuery({
    queryKey: keys.revenues(period, undefined),
    queryFn: () => api.listRevenues(period, undefined),
    enabled,
  });
}

/** Guarda los ajustes sin vaciar el formulario (se guarda solo, «✓ Guardado»). */
export function useAutoSaveSettings(): UseMutationResult<TenantSettings, Error, TenantSettings> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSettings) => api.updateSettings(input),
    onSuccess: (saved) => {
      qc.setQueryData(keys.settings, saved);
      invalidateCosting(qc);
      invalidateFinance(qc);
    },
  });
}

export function useUpdateLocationConfig(): UseMutationResult<Location, Error, { locationId: string } & LocationConfigPatch> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, ...patch }) => api.updateLocationConfig(locationId, patch),
    onSuccess: (saved) => {
      qc.setQueryData<MeResponse | undefined>(keys.me, (me) =>
        me ? { ...me, locations: me.locations.map((l) => (l.locationId === saved.locationId ? saved : l)) } : me,
      );
      void qc.invalidateQueries({ queryKey: keys.locations });
      invalidateFinance(qc);
    },
  });
}

export function useSetMemberLocations(): UseMutationResult<{ membership: Membership }, Error, { userId: string; locationIds: string[] }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, locationIds }) => api.setMemberLocations(userId, locationIds),
    onSuccess: invalidateTeam(qc),
  });
}

export function useUpdateMyName(): UseMutationResult<{ name: string }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.updateMyName(name),
    onSuccess: ({ name }) => {
      qc.setQueryData<MeResponse | undefined>(keys.me, (me) => (me ? { ...me, user: { ...me.user, name } } : me));
    },
  });
}

export function useUpdateMyAppearance(): UseMutationResult<{ apariencia: Apariencia }, Error, Apariencia> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apariencia: Apariencia) => api.updateMyAppearance(apariencia),
    onSuccess: ({ apariencia }) => {
      qc.setQueryData<MeResponse | undefined>(keys.me, (me) => (me ? { ...me, user: { ...me.user, apariencia } } : me));
    },
  });
}

export function useAccountDeletion(): {
  pedir: UseMutationResult<Tenant, Error, string>;
  cancelar: UseMutationResult<Tenant, Error, void>;
} {
  const qc = useQueryClient();
  const alGuardar = (tenant: Tenant) =>
    qc.setQueryData<MeResponse | undefined>(keys.me, (me) => (me ? { ...me, tenant } : me));
  return {
    pedir: useMutation({ mutationFn: (confirmName: string) => api.requestAccountDeletion(confirmName), onSuccess: alGuardar }),
    cancelar: useMutation({ mutationFn: () => api.cancelAccountDeletion(), onSuccess: alGuardar }),
  };
}
